import { Box, Grid } from '@mui/material'
import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { DateTime } from 'luxon';
import IbssDialog from '../../../../Components/Dialogs/BaseDialog/IbssDialog';
import { InputHeight, QuestionType } from './DataModels';
import { DateHelper } from '../../../../Common/DateHelper';
import IbssCheckBox from '../../../../Components/Inputs/CheckBox/IbssCheckBox';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import IbssTextField from '../../../../Components/Inputs/TextField/IbssTextField';
import IbssInputDropDown from '../../../../Components/Inputs/SelectList/IbssInputDropDown';
import TickCross from '../../../../Components/Icons/TickCross';
import { TypeHelper } from '../../../../Common/TypeHelper';

export default class EditVisitorStatus extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private currentUser = '';

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            showAnswer: false,
            selectedQuestion: {
                questionIdOrIndex: '',
                type: QuestionType.NotSet,
                value: '',
                policyWording: '',
                policyConfirmationText: '',
                placeHolderText: '',
                helperText: '',
                options: [],
                height: '',
                maxLength: 0,
                answer: '',
                answeredAt: DateHelper.null(),
                answeredBy: '',
            },
        };
    }

    public componentDidMount(): void
    {
        this.currentUser = appContext().localStorageProvider.getUserDetails().email.toLocaleLowerCase();
    }

    private canEditQuestion(question: IQuestion): boolean
    {
        return (question.answeredAt.isNull() || question.answeredBy.toLocaleLowerCase() == this.currentUser);
    }

    private isQuestionAnswered(question: IQuestion): boolean
    {
        return !question.answeredAt.isNull();
    }

    private handleQuestionClick(question: IQuestion): void
    {
        this.setState({ showAnswer: true, selectedQuestion: question });
    }

    private handleAnswerChange(answer: string): void
    {
        this.setState({
            selectedQuestion: {
                ...this.state.selectedQuestion,
                answer: answer,
            }
        });
    }

    private handleCloseAnswer(): void
    {
        this.setState({ showAnswer: false });
    }

    private handleDeleteAnswer(): void
    {
        this.setState({ showAnswer: false });
        this.props.onDelete(this.state.selectedQuestion.questionIdOrIndex);
    }

    private handleSubmitAnswer(answer: string): void
    {
        if (answer.trim() == '')
        {
            this.handleDeleteAnswer();
            return;
        }
        this.setState({ showAnswer: false });
        this.props.onSubmit(this.state.selectedQuestion.questionIdOrIndex, answer);
    }

    public render(): JSX.Element
    {
        const numOfQuestions = this.props.questions.length;

        return (<div className='edit-visit'>
            <Box className="tablePanel">
                <h3>{this.labels.funcManageQuestionHeader_S}</h3>
                {this.props.questions.length == 0 && (<div>(No questions)</div>)}
                {this.props.questions.map((i, index) => this.renderQuestionOrNonQuestion(i, index == numOfQuestions - 1))}
            </Box>
            <IbssDialog
                aria-modal='true'
                aria-label={this.labels.funcManuallyConfirmVisitorRequirements_L}
                fullWidth
                open={this.state.showAnswer}
                onClose={() => this.handleCloseAnswer()}
                header={this.labels.funcManuallyConfirmVisitorRequirements_L}
                dialogContent={this.renderAnswerContent()}
                footer={this.renderAnswerFooter()}
            />
        </div>);
    }

    private renderQuestionOrNonQuestion(question: IQuestion, isLast: boolean): JSX.Element
    {
        let component: JSX.Element;
        switch (question.type)
        {
            case QuestionType.NonQuestionTypeHeader:
                component = this.renderHeading(question);
                break;
            case QuestionType.NonQuestionTypeSubHeader:
                component = this.renderSubHeading(question);
                break;
            case QuestionType.NonQuestionTypeImage:
                component = this.renderImage(question);
                break;
            case QuestionType.QuestionTypeOpen:
            case QuestionType.QuestionTypeTrueFalse:
            case QuestionType.QuestionTypeMultiOptionSingleSelect:
            case QuestionType.QuestionTypePolicy:
                component = this.renderQuestion(question);
                break;
            default:
                component = <></>;
                break;
        }

        return (
            <>
                {component}
                {!isLast && <hr className='edit-visit__question-divider' />}
            </>
        );
    }

    private renderHeading(question: IQuestion): JSX.Element
    {
        return <div className='edit-visit__question edit-visit--heading'>{question.value}</div>;
    }

    private renderSubHeading(question: IQuestion): JSX.Element
    {
        return <div className='edit-visit__question edit-visit--sub-heading'>{question.value}</div>;
    }

    private renderImage(question: IQuestion): JSX.Element
    {
        return <img src={question.value} alt={question.helperText} className='edit-visit__question edit-visit--image' />;
    }

    private renderQuestion(question: IQuestion): JSX.Element
    {
        const canEdit = this.canEditQuestion(question);
        const isAnswered = this.isQuestionAnswered(question);

        return (
            <Grid container onClick={() => this.handleQuestionClick(question)} className='edit-visit__question'>
                <Grid item md={12} lg={5}>
                    {!canEdit && <img src='/images/readOnlyField.svg' alt='Read-only' width='16px' height='16px' style={{ marginRight: '16px' }} />}
                    <span>{question.value}</span>
                </Grid>
                {isAnswered && <Grid item md={12} lg={2} className='edit-visit__question-info'>{question.answeredAt.toLocaleDateString()}</Grid>}
                {isAnswered && <Grid item md={12} lg={4} className='edit-visit__question-info'>{question.answeredBy}</Grid>}
                {!isAnswered && <Grid item md={12} lg={6} />}
                <Grid item md={12} lg={1} className='edit-visit__question-checkbox'>
                    <IbssCheckBox checked={isAnswered} disabled={!canEdit} />
                </Grid>
            </Grid>
        );
    }

    private renderAnswerContent(): JSX.Element
    {
        const { selectedQuestion } = this.state;
        const isAnswered = this.isQuestionAnswered(selectedQuestion);

        return (
            <Grid container direction='column' spacing={2}>
                {isAnswered &&
                    <Grid item container direction='row'>
                        <Grid item className='edit-visit__answer-info' xs={6}>{selectedQuestion.answeredAt.toLocaleDateString()}</Grid>
                        <Grid item className='edit-visit__answer-info' xs={6} textAlign='right'>{selectedQuestion.answeredBy}</Grid>
                    </Grid>
                }
                {this.renderAnswer()}
            </Grid>
        );
    }

    private renderAnswer(): JSX.Element
    {
        switch (this.state.selectedQuestion.type)
        {
            case QuestionType.QuestionTypeOpen:
                return this.renderFreeTextAnswer();
            case QuestionType.QuestionTypeTrueFalse:
                return this.renderBooleanAnswer();
            case QuestionType.QuestionTypeMultiOptionSingleSelect:
                return this.renderOptionsAnswer();
            case QuestionType.QuestionTypePolicy:
                return this.renderPolicyAnswer();
            default:
                return <></>;
        }
    }

    private renderFreeTextAnswer(): JSX.Element
    {
        const { selectedQuestion } = this.state;
        const canEdit = this.canEditQuestion(selectedQuestion);
        const numOfRows = (selectedQuestion.height == 'multi' ? 8 : 1);

        return (
            <>
                <Grid item>{selectedQuestion.value}</Grid>
                <Grid item>
                    <IbssTextField
                        label={selectedQuestion.placeHolderText}
                        helperText={selectedQuestion.helperText}
                        fullWidth
                        multiline={numOfRows > 1}
                        rows={numOfRows}
                        value={selectedQuestion.answer}
                        disabled={!canEdit}
                        onChange={e => this.handleAnswerChange(e.target.value)}
                        maxLength={selectedQuestion.maxLength || 500}
                    />
                </Grid>
            </>
        );
    }

    private renderBooleanAnswer(): JSX.Element
    {
        const { selectedQuestion } = this.state;
        const canEdit = this.canEditQuestion(selectedQuestion);
        const answerAsBoolean = TypeHelper.toBoolean(selectedQuestion.answer);

        return (
            <>
                <Grid item>{selectedQuestion.value} {answerAsBoolean == null ? <></> : <TickCross value={answerAsBoolean} />}</Grid>
                {selectedQuestion.helperText && <Grid item><span className='edit-visit__helper-text'>{selectedQuestion.helperText}</span></Grid>}
            </>
        );
    }

    private renderOptionsAnswer(): JSX.Element
    {
        const { selectedQuestion } = this.state;
        const canEdit = this.canEditQuestion(selectedQuestion);
        const options = selectedQuestion.options.map(i => ({ value: i, label: i }));

        return (
            <>
                <Grid item>{selectedQuestion.value}</Grid>
                <Grid item>
                    <IbssInputDropDown
                        id={selectedQuestion.questionIdOrIndex}
                        value={selectedQuestion.answer}
                        helperText={selectedQuestion.helperText}
                        inputLabel={selectedQuestion.placeHolderText}
                        options={options}
                        disabled={!canEdit}
                        onChange={e => this.handleAnswerChange(e.target.value as string)}
                    />
                </Grid>
            </>
        );
    }

    private renderPolicyAnswer(): JSX.Element
    {
        const { selectedQuestion } = this.state;
        const answerAsBoolean = TypeHelper.toBoolean(selectedQuestion.answer);

        return (
            <>
                <Grid item>{selectedQuestion.policyWording}</Grid>
                <Grid item>{selectedQuestion.policyConfirmationText} <TickCross value={answerAsBoolean ?? false} /></Grid>
                {selectedQuestion.helperText && <Grid item><span className='edit-visit__helper-text'>{selectedQuestion.helperText}</span></Grid>}
            </>
        );
    }

    private renderAnswerFooter(): JSX.Element
    {
        switch (this.state.selectedQuestion.type)
        {
            case QuestionType.QuestionTypeTrueFalse:
                return this.renderYesNoFooter();
            case QuestionType.QuestionTypePolicy:
                return this.renderConfirmFooter();
            default:
                return this.renderOkFooter();
        }
    }

    private renderYesNoFooter(): JSX.Element
    {
        const canEdit = this.canEditQuestion(this.state.selectedQuestion);
        return <>
            <IbssButton
                color="secondary"
                variant="contained"
                className="mr-1"
                onClick={() => this.handleCloseAnswer()}
            >
                {this.labels.funcCloseAnswer_S}
            </IbssButton>
            {canEdit &&
                <>
                    <IbssButton
                        color="error"
                        variant="contained"
                        className="mr-1"
                        onClick={() => this.handleDeleteAnswer()}
                    >
                        {this.labels.funcRemoveAnswer_S}
                    </IbssButton>
                    <IbssButton
                        color="primary"
                        variant="contained"
                        className="mr-1"
                        onClick={() => this.handleSubmitAnswer('false')}
                    >
                        {this.labels.funcCancelAnswer_S}
                    </IbssButton>
                    <IbssButton
                        color="primary"
                        variant="contained"
                        onClick={() => this.handleSubmitAnswer('true')}
                    >
                        {this.labels.funcSubmitAnswer_S}
                    </IbssButton>
                </>
            }
        </>
    }

    private renderConfirmFooter(): JSX.Element
    {
        const canEdit = this.canEditQuestion(this.state.selectedQuestion);
        return <>
            <IbssButton
                color="secondary"
                variant="contained"
                className="mr-1"
                onClick={() => this.handleCloseAnswer()}
            >
                {this.labels.funcCloseAnswer_S}
            </IbssButton>
            {canEdit &&
                <>
                    <IbssButton
                        color="error"
                        variant="contained"
                        className="mr-1"
                        onClick={() => this.handleDeleteAnswer()}
                    >
                        {this.labels.funcRemoveAnswerConfirmation_S}
                    </IbssButton>
                    <IbssButton
                        color="primary"
                        variant="contained"
                        onClick={() => this.handleSubmitAnswer('true')}
                    >
                        {this.labels.funcSubmitAnswerConfirmation_S}
                    </IbssButton>
                </>
            }
        </>
    }

    private renderOkFooter(): JSX.Element
    {
        const canEdit = this.canEditQuestion(this.state.selectedQuestion);
        return <>
            <IbssButton
                color="secondary"
                variant="contained"
                className="mr-1"
                onClick={() => this.handleCloseAnswer()}
            >
                {this.labels.funcCloseAnswer_S}
            </IbssButton>
            {canEdit &&
                <>
                    <IbssButton
                        color="error"
                        variant="contained"
                        className="mr-1"
                        onClick={() => this.handleDeleteAnswer()}
                    >
                        {this.labels.funcRemoveAnswer_S}
                    </IbssButton>
                    <IbssButton
                        color="primary"
                        variant="contained"
                        onClick={() => this.handleSubmitAnswer(this.state.selectedQuestion.answer)}
                    >
                        {this.labels.HubLabelOk}
                    </IbssButton>
                </>
            }
        </>
    }
}

export interface IProps
{
    questions: IQuestion[];
    onDelete: (questionIdOrIndex: string | number) => void;
    onSubmit: (questionIdOrIndex: string | number, answer: string) => void;
}

export interface IQuestion
{
    questionIdOrIndex: string;
    type: QuestionType;
    value: string;
    policyWording: string;
    policyConfirmationText: string;
    placeHolderText: string;
    helperText: string;
    options: string[];
    height: InputHeight;
    maxLength: number;
    answer: string;
    answeredAt: DateTime;
    answeredBy: string;
}

export interface IState
{
    showAnswer: boolean;
    selectedQuestion: IQuestion;
}
